import React, { useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import styled from "styled-components";
import Box from "../Box";
import { formatPercentage } from "../../utils/numberUtils";

type NPSPieChartProps = {
  promotersPercentage: number;
  detractorsPercentage: number;
  neutralsPercentage: number;
};

const ChartContainer = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CenterValue = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, calc(-50% + 6px));
  font-size: 28px;
  font-weight: bold;
  pointer-events: none;
  user-select: none;
  line-height: 1;
`;

const TAB_COLOR = [
  "#4CCD8A", /* green-medium */
  "#E5E9F0", /* grey-3 */
  "#E81E26", /* red-medium */
];

const NPSPieChart: React.FC<NPSPieChartProps> = ({
  promotersPercentage,
  detractorsPercentage,
  neutralsPercentage,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  let valueNPS = "-";
  if (promotersPercentage - detractorsPercentage !== 0) {
    valueNPS = formatPercentage(promotersPercentage - detractorsPercentage, "");
  } else if (
    promotersPercentage - detractorsPercentage === 0 &&
    neutralsPercentage > 0
  ) {
    valueNPS = "0";
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const chartInstance = new Chart(canvas, {
      type: "doughnut",
      data: {
        labels: ["Promoteurs", "Neutres", "Détracteurs"],
        datasets: [
          {
            data: [promotersPercentage, neutralsPercentage, detractorsPercentage],
            backgroundColor: TAB_COLOR,
            hoverBackgroundColor: TAB_COLOR,
            borderWidth: 0,
            hoverBorderWidth: 0,
            spacing: 2,
            borderRadius: 4,
            hoverOffset: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "65%",
        animation: {
          duration: 400,
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
        },
        events: [],
      },
    });

    return () => {
      chartInstance.destroy();
    };
  }, [promotersPercentage, neutralsPercentage, detractorsPercentage]);

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <ChartContainer>
        <canvas ref={canvasRef} width={200} height={200} />
        <CenterValue>{valueNPS}</CenterValue>
      </ChartContainer>
    </Box>
  );
};

export default NPSPieChart;
